import {
  AssetShare,
  CalculosInventario,
  PartilhaResultado,
} from '../types';

type ShareType =
  | 'Meação'
  | 'Herança'
  | 'Doação'
  | 'Legado'
  | 'Parte disponível'
  | 'Usufruto'
  | 'Nua-propriedade';

/** Linha do detalhamento de cálculo (drawer / exportação). */
export type BreakdownRow = {
  label: string;
  memo?: string;
  value: number | string;
  subtle?: boolean;
  isOperator?: boolean;
  isHeader?: boolean;
  isSubtotal?: boolean;
};

export type CalculationBreakdown = {
  title: string;
  rows: BreakdownRow[];
  totalLabel?: string;
  totalValue?: number;
  note?: string;
};

const EPS = 0.005;

/** Formata percentual padrão pt-BR com 2 casas. */
export function formatPercent(decimal: number): string {
  return `${(decimal * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function valuationPurposeLabel(p: AssetShare['valuationPurpose']): string {
  switch (p) {
    case 'DATA_OBITO':
      return 'data do óbito';
    case 'DATA_ESCRITURA':
      return 'valor venal atualizado (escritura)';
    case 'DATA_PARTILHA':
      return 'declarado na escritura (partilha)';
    case 'GENERIC':
      return 'valor de referência';
    default:
      return 'valor de referência';
  }
}

export function buildAssetShareBreakdownRows(
  shares: AssetShare[],
  formatCurrency: (v: number) => string,
  includeTypePrefix: boolean = false,
): BreakdownRow[] {
  return shares.map((s) => {
    const desc = s.assetDescription || s.categoryLabel || 'Bem';
    const catSuffix = s.categoryLabel ? ` · ${s.categoryLabel}` : '';
    const typePrefix = includeTypePrefix && s.type ? `${s.type} · ` : '';
    const label = `${typePrefix}${desc}${catSuffix}`;

    const hasFraction =
      s.baseValue != null && s.fraction != null && Math.abs((s.fraction ?? 1) - 1) > 0.001;
    const hasBase = s.baseValue != null;

    if (hasFraction) {
      const memoLines = [
        `${formatCurrency(s.baseValue as number)} × ${formatPercent(s.fraction as number)}`,
      ];
      if (s.fractionLabel) memoLines.push(s.fractionLabel);
      memoLines.push(`base: ${valuationPurposeLabel(s.valuationPurpose)}`);
      return {
        label,
        memo: memoLines.join('\n'),
        value: s.value || 0,
      };
    }

    if (hasBase) {
      const memoLines: string[] = [];
      if (s.fractionLabel) memoLines.push(s.fractionLabel);
      memoLines.push(`base: ${valuationPurposeLabel(s.valuationPurpose)}`);
      return {
        label,
        memo: memoLines.join('\n'),
        value: s.value || 0,
      };
    }

    return { label, value: s.value || 0 };
  });
}

export function buildSubtotalDetailRows(
  partilha: PartilhaResultado,
  formatCurrency: (v: number) => string,
): BreakdownRow[] {
  const allShares = partilha.assetShares || [];
  const order: ShareType[] = [
    'Meação',
    'Usufruto',
    'Herança',
    'Nua-propriedade',
    'Doação',
    'Legado',
    'Parte disponível',
  ];
  const rows: BreakdownRow[] = [];
  let anyShareRendered = false;

  order.forEach((t) => {
    const sharesOfType = allShares.filter((s) => s.type === t);
    if (sharesOfType.length === 0) return;
    const subtotalT = sharesOfType.reduce((acc, s) => acc + (s.value || 0), 0);
    if (Math.abs(subtotalT) <= EPS) return;
    const detail = buildAssetShareBreakdownRows(sharesOfType, formatCurrency, true);
    detail.forEach((r) => rows.push(r));
    anyShareRendered = true;
  });

  if (!anyShareRendered && (partilha.valorRecebidoBruto || 0) > EPS) {
    rows.push({ label: 'Doação em vida (liberalidades)', value: partilha.valorRecebidoBruto || 0 });
  }

  return rows;
}

export function resolveItcmdAmounts(partilha: PartilhaResultado): {
  itcmdCm: number;
  itcmdDoacao: number;
} {
  const subtotal = partilha.valorRecebidoBruto || 0;
  const liquidoPosItcmd = partilha.valorRecebidoLiquido ?? subtotal;
  const itcmdImplicito = Math.max(0, subtotal - liquidoPosItcmd);

  let itcmdDoacao = partilha.itcmdDoacao ?? 0;
  let itcmdCm =
    partilha.itcmdCausaMortis ??
    Math.max(0, (partilha.itcmdDevido ?? 0) - itcmdDoacao);

  if (itcmdCm <= EPS && itcmdDoacao <= EPS && (partilha.itcmdDevido ?? 0) > EPS) {
    itcmdCm = Math.max(0, (partilha.itcmdDevido ?? 0) - itcmdDoacao);
  }

  if (itcmdCm <= EPS && itcmdDoacao <= EPS && itcmdImplicito > EPS) {
    itcmdCm = itcmdImplicito;
  }

  return { itcmdCm, itcmdDoacao };
}

/** Líquido final após ITCMD, emolumentos, RI e honorários (mesma fórmula da matriz). */
export function computeLiquidoFinalPartilha(
  partilha: PartilhaResultado,
  calculos: CalculosInventario,
  partilhas: PartilhaResultado[],
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >,
): number {
  const emol = resolveEmolumentosDevidos(partilha, calculos, partilhas);
  const registro = custosTributosPorPessoa[String(partilha.herdeiroId)]?.registroImoveis ?? 0;
  const honorarios =
    custosTributosPorPessoa[String(partilha.herdeiroId)]?.honorariosAdvocaticios ?? 0;
  return (partilha.valorRecebidoLiquido ?? 0) - emol - registro - honorarios;
}

export function resolveEmolumentosDevidos(
  partilha: PartilhaResultado,
  calculos: CalculosInventario,
  partilhas: PartilhaResultado[],
): number {
  const fromPartilha = partilha.emolumentosDevidos ?? 0;
  if (Math.abs(fromPartilha) > EPS) return fromPartilha;

  const totalGeral = calculos.emolumentosTotal || 0;
  if (totalGeral <= EPS) return 0;

  const totalBrutoGeral = partilhas.reduce((s, p) => s + (p.valorRecebidoBruto || 0), 0);
  const quinhaoPessoa = partilha.valorRecebidoBruto || 0;
  if (totalBrutoGeral <= EPS) return 0;
  return totalGeral * (quinhaoPessoa / totalBrutoGeral);
}

function pushDeductionRow(
  rows: BreakdownRow[],
  label: string,
  amount: number,
  memo?: string,
): void {
  if (Math.abs(amount) <= EPS) return;
  rows.push({ label, memo, value: -Math.abs(amount) });
}

/**
 * Breakdown do Total a receber (líquido) para uma pessoa.
 * Mesmo conteúdo exibido no drawer da célula "Total a receber (líquido)".
 */
export function getTotalLiquidoBreakdown({
  partilha,
  calculos,
  custosTributosPorPessoa,
  formatCurrency,
  partilhas = [],
}: {
  partilha: PartilhaResultado;
  calculos: CalculosInventario;
  custosTributosPorPessoa: Record<
    string,
    { registroImoveis: number; honorariosAdvocaticios: number }
  >;
  formatCurrency: (v: number) => string;
  /** Todas as partilhas — usado no rateio de emolumentos/RI/honorários. */
  partilhas?: PartilhaResultado[];
}): CalculationBreakdown {
  const subtotal = partilha.valorRecebidoBruto || 0;
  const { itcmdCm, itcmdDoacao } = resolveItcmdAmounts(partilha);
  const emol = resolveEmolumentosDevidos(partilha, calculos, partilhas);
  const registro = custosTributosPorPessoa[String(partilha.herdeiroId)]?.registroImoveis ?? 0;
  const honorarios =
    custosTributosPorPessoa[String(partilha.herdeiroId)]?.honorariosAdvocaticios ?? 0;
  const liquido = computeLiquidoFinalPartilha(
    partilha,
    calculos,
    partilhas,
    custosTributosPorPessoa,
  );

  const rows: BreakdownRow[] = [];
  const aliquota = calculos.itcmdAliquota ?? 0.04;

  const subtotalRows = buildSubtotalDetailRows(partilha, formatCurrency);
  if (subtotalRows.length > 0) {
    subtotalRows.forEach((r) => rows.push(r));
  } else if (subtotal > EPS) {
    rows.push({ label: 'Quinhão bruto', value: subtotal });
  }

  if (subtotal > EPS) {
    rows.push({
      label: 'Subtotal — quinhão bruto',
      memo: 'Soma das linhas acima (antes das deduções)',
      value: subtotal,
      isSubtotal: true,
    });
  }

  const deductionRows: BreakdownRow[] = [];

  if (itcmdCm > EPS) {
    const baseTributavel =
      partilha.itcmdBaseCausaMortisTributavel ?? partilha.itcmdBaseCausaMortis ?? 0;
    pushDeductionRow(
      deductionRows,
      '(−) ITCMD causa mortis',
      itcmdCm,
      baseTributavel > EPS
        ? `${formatCurrency(baseTributavel)} × ${formatPercent(aliquota)}\nbase: data do óbito`
        : undefined,
    );
  }

  if (itcmdDoacao > EPS) {
    const baseDoacao = partilha.itcmdBaseDoacaoBruta ?? 0;
    pushDeductionRow(
      deductionRows,
      '(−) ITCMD liberalidades',
      itcmdDoacao,
      baseDoacao > EPS
        ? `${formatCurrency(baseDoacao)} × ${formatPercent(aliquota)}\nbase: valor venal atualizado`
        : undefined,
    );
  }

  if (emol > EPS) {
    const totalGeral = calculos.emolumentosTotal || 0;
    const totalBrutoGeral = partilhas.reduce((s, p) => s + (p.valorRecebidoBruto || 0), 0);
    const quinhaoPessoa = partilha.valorRecebidoBruto || 0;
    const proporcao = totalBrutoGeral > EPS ? quinhaoPessoa / totalBrutoGeral : 0;
    pushDeductionRow(
      deductionRows,
      '(−) Emolumentos cartório',
      emol,
      totalGeral > EPS
        ? [
            `${formatCurrency(totalGeral)} × ${formatPercent(proporcao)}`,
            `quinhão: ${formatCurrency(quinhaoPessoa)} de ${formatCurrency(totalBrutoGeral)}`,
            `base: Tabela Notas SP${calculos.emolumentosFaixa ? ` · faixa ${calculos.emolumentosFaixa}` : ''}`,
          ].join('\n')
        : undefined,
    );
  }

  if (registro > EPS) {
    const totalGeral = calculos.registroImoveisTotal || 0;
    const proporcao = totalGeral > EPS ? registro / totalGeral : 0;
    pushDeductionRow(
      deductionRows,
      '(−) Registro de Imóveis',
      registro,
      totalGeral > EPS
        ? [
            `${formatCurrency(totalGeral)} × ${formatPercent(proporcao)}`,
            'base: custas Tabela II SP + pré-notação',
          ].join('\n')
        : undefined,
    );
  }

  if (honorarios > EPS) {
    const patrimonioLiquido = calculos.totalPatrimonioLiquido || 0;
    const totalGeral = patrimonioLiquido * 0.08;
    const proporcao = totalGeral > EPS ? honorarios / totalGeral : 0;
    pushDeductionRow(
      deductionRows,
      '(−) Honorários advocatícios',
      honorarios,
      patrimonioLiquido > EPS
        ? [
            `${formatCurrency(patrimonioLiquido)} × ${formatPercent(0.08)}`,
            `${formatCurrency(totalGeral)} × ${formatPercent(proporcao)}`,
            'base: estimativa contratual (referência 8%)',
          ].join('\n')
        : undefined,
    );
  }

  if (deductionRows.length > 0) {
    rows.push({ label: 'Deduções', value: '', isHeader: true });
    deductionRows.forEach((r) => rows.push(r));
  }

  return {
    title: `Total a receber (líquido) — ${partilha.herdeiroNome || '—'}`,
    rows,
    totalLabel: 'Total a receber',
    totalValue: liquido,
  };
}
